import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { authApi, type AppUser } from "../services/authApi";
import { AUTH_PERMISSIONS_CHANGED_EVENT, AUTH_SESSION_EXPIRED_EVENT, getAuthToken, getAuthTokenExpiry } from "../services/apiClient";

type AuthContextValue = {
  user: AppUser | null;
  loading: boolean;
  sessionMessage: string;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(Boolean(getAuthToken()));
  const [sessionMessage, setSessionMessage] = useState("");

  const expireSession = () => {
    authApi.logout();
    setUser(null);
    setSessionMessage("Istuntosi on vanhentunut tai käyttäjätunnusta ei enää tunnisteta. Kirjaudu uudelleen.");
  };

  useEffect(() => {
    if (!getAuthToken()) return;
    authApi.me()
      .then((result) => setUser(result.user))
      .catch(() => expireSession())
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const checkExpiry = () => {
      const expiresAt = getAuthTokenExpiry();
      if (expiresAt !== null && expiresAt <= Date.now()) expireSession();
    };
    const sessionExpired = () => expireSession();
    const permissionsChanged = () => {
      authApi.me().then((result) => setUser(result.user)).catch(() => undefined);
    };
    checkExpiry();
    const interval = window.setInterval(checkExpiry, 30_000);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, sessionExpired);
    window.addEventListener(AUTH_PERMISSIONS_CHANGED_EVENT, permissionsChanged);
    window.addEventListener("focus", checkExpiry);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, sessionExpired);
      window.removeEventListener(AUTH_PERMISSIONS_CHANGED_EVENT, permissionsChanged);
      window.removeEventListener("focus", checkExpiry);
    };
  }, []);

  const login = async (email: string, password: string) => {
    const authenticated = await authApi.login(email, password);
    setUser(authenticated);
    setSessionMessage("");
  };

  const logout = () => {
    authApi.logout();
    setUser(null);
    setSessionMessage("");
  };

  const refreshUser = async () => {
    const result = await authApi.me();
    setUser(result.user);
  };

  return <AuthContext.Provider value={{ user, loading, sessionMessage, login, logout, refreshUser }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
